import SignIn from '@/pages/SignIn';
import type React from 'react';
import {createContext, useCallback, useEffect, useState} from 'react';
import {useLocation, useParams} from "react-router-dom";
import Interested from "@/pages/Interested";
import type {JWTUser} from "@/types/JWTUser";

type Props = {
    children?: React.ReactNode;
};

type LoginResponse = {
    jwt: string;
    displayName: string;
};

export const JwtContext = createContext<JWTUser | null>(null);

const JWTProvider: React.FC<Props> = ({children}: Props) => {
    const {pathname} = useLocation();
    const {actionItem, activityId} = useParams();
    const [user, setUser] = useState<JWTUser | null>();

    useEffect(() => {
        const jwtUser = localStorage.getItem('JWTUser');

        if (jwtUser !== null) {
            setUser(JSON.parse(jwtUser) as JWTUser);
        } else {
            setUser(null);
        }
    }, []);

    if (user === undefined) {
        return <div>Loading ...</div>;
    }

    if (user === null) {
        if (pathname.includes('/interested-form')) {
            return (
                <>
                    <Interested />
                </>
            );
        }

        return (
            <>
                <SignIn />
            </>
        );
    }

    return <JwtContext.Provider value={user}>{children}</JwtContext.Provider>;
};

export type ApiFetch = (url: string, init?: RequestInit) => Promise<Response>;

type AuthenticateUser = (username: string, password: string) => Promise<JWTUser | null>;

export const useAuthenticateUser = (): AuthenticateUser => {
    return useCallback(async (username: string, password: string): Promise<JWTUser | null> => {
        const init: RequestInit = {
            headers: new Headers({Accept: 'application/json', 'Content-Type': 'application/json'}),
            method: 'POST',
        };
        init.body = JSON.stringify({
            username: username,
            password: password,
        });

        const url = new URL('/student/authenticate', import.meta.env.VITE_APP_API_ENDPOINT);
        const response = await fetch(url.toString(), init);

        if (response.status !== 200) {
            return null;
        }

        const data = (await response.json()) as LoginResponse;
        const user = {
            displayName: data.displayName,
            accessToken: data.jwt,
        };

        localStorage.setItem('JWTUser', JSON.stringify(user));

        return user;
    }, []);
};

export const signOut = (): void => {
    localStorage.removeItem('JWTUser');
};

export default JWTProvider;
